import React from 'react';

import { Group, Paper, Space, Stack, Text, UnstyledButton, useMantineTheme } from '@mantine/core';
import { IconChevronRight, IconInfoCircle } from '@tabler/icons-react';
import { EnrichedWorkout, MovementResult, ResultsState } from 'components/Workout/logging/types';
import ReadonlyWorkoutNotesRTE from 'components/workout-program/ReadonlyWorkoutNotesRTE';
import { useNavigate } from 'react-router-dom';
import { WorkoutLevelEnum } from 'types/enums';

import WorkoutNotesAccordion from './WorkoutNotesAccordion';
import WorkoutSectionCard from './WorkoutSectionCard';

interface LogResultsPanelProps {
  workout: EnrichedWorkout;
  kiloClassId: string;
  results: ResultsState;
  sectionLevels: Record<string, WorkoutLevelEnum | null>;
  notes: string;
  onResultChange: (movementId: string, result: MovementResult) => void;
  onResultCommit: (movementId: string, result: MovementResult) => Promise<void>;
  onLevelChange: (sectionId: string, level: WorkoutLevelEnum) => void;
  onLevelCommit: (sectionId: string, level: WorkoutLevelEnum) => Promise<void>;
  onNotesChange: (notes: string) => void;
  onNotesCommit: (notes: string) => void;
}

const LogResultsPanel = ({
  workout,
  kiloClassId,
  results,
  sectionLevels,
  notes,
  onResultChange,
  onResultCommit,
  onLevelChange,
  onLevelCommit,
  onNotesChange,
  onNotesCommit,
}: LogResultsPanelProps): JSX.Element => {
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const hasTrackedMovements = workout.sections.some(section => section.movements.length > 0);

  return (
    <Stack spacing='lg'>
      <Paper withBorder p='md' radius='md'>
        <Group position='apart' noWrap>
          <Text fw={600} size='lg'>
            {workout.name}
          </Text>
          {hasTrackedMovements && (
            <UnstyledButton
              onClick={() => navigate(`/classes/${kiloClassId}/leaderboard`)}
              sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
            >
              <Text color='kiloBlue' weight={600} size='sm'>
                Leaderboard
              </Text>
              <IconChevronRight size={16} color={theme.colors.kiloBlue[6]} />
            </UnstyledButton>
          )}
        </Group>
      </Paper>

      {workout.athleteNotes && (
        <Paper withBorder p='md' radius='md'>
          <Group spacing='xs' mb={4}>
            <IconInfoCircle size={16} color={theme.colors.blue[6]} />
            <Text size='sm' weight={600}>
              Athlete Notes
            </Text>
          </Group>
          <ReadonlyWorkoutNotesRTE workoutNotes={workout.athleteNotes} mih={0} bordered={false} />
        </Paper>
      )}

      {workout.sections.map((section, index) => (
        <WorkoutSectionCard
          key={section.workoutSectionId}
          section={section}
          sectionIndex={index}
          results={results}
          level={sectionLevels[section.workoutSectionId] ?? null}
          onResultChange={onResultChange}
          onResultCommit={onResultCommit}
          onLevelChange={level => onLevelChange(section.workoutSectionId, level)}
          onLevelCommit={level => onLevelCommit(section.workoutSectionId, level)}
        />
      ))}

      <WorkoutNotesAccordion value={notes} onChange={onNotesChange} onBlur={onNotesCommit} />
      <Space h={80} />
    </Stack>
  );
};

export default LogResultsPanel;
